// @react
import React, { ReactElement } from 'react'
// @types
// @libraries
import clsx from 'clsx'
import useResizeObserver from 'use-resize-observer'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
// @components
import Grid from '@bit/atd.web.grid'
import Button from '@bit/atd.web.button'
import Spacer from '@bit/atd.web.spacer'
import Icon from '@bit/atd.web.icons'
// @card parts
import Wrap from '../../Parts/Wrap'
import Title from '../../Parts/Title'
import Avatar from '../../Parts/Avatar'
import { PropsType as CardProps } from '../../index'
import Loader from '@bit/atd.web.loaders.base'

const useLoaderStyles = makeStyles({
  wrapper: {
    position: 'relative',
  },
  loader: {
    position: 'absolute',
    top: 'calc(50% - 20px)',
    right: 'calc(50% - 20px)',
    zIndex: 0,
  },
})

const useStyles = makeStyles({
  wrap: {
    alignItems: 'flex-start',
    padding: '16px',
  },
  header: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  avatarWrapper: {
    marginBottom: '10px',
    '& + $staffWrapper + $textWrapper': {
      marginTop: '12px',
    },
  },
  atdBadge: {
    height: '23px',
    width: '34px',
    '& path': {
      fill: `#999`,
    },
  },
  textWrapper: {
    marginBottom: '24px',
  },
  title: {
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '22px',
    color: `#2E2D31`,
    '& + $subtitle': {
      marginTop: '2px',
    },
  },
  subtitle: {
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '16px',
    color: '#7c7c7c',
  },
  staffWrapper: {},
  button: {
    marginTop: 'auto',
  },
  isFollowing: {
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '3px',
      background:
        'linear-gradient(90deg, #FFB700 0%, #FF4D00 52.98%, #C10013 98.75%)',
    },
  },
  isNoAvatar: {
    '& $title': {
      fontSize: '18px',
      lineHeight: '24px',
      '& + $subtitle': {
        marginTop: '4px',
      },
    },
    '& $subtitle': {
      fontSize: '14px',
      lineHeight: '16px',
    },
  },
  isLayoutHorizontal: {
    alignItems: 'center',
    marginBottom: '24px',
    '& $atdBadge': {
      marginLeft: 'auto',
    },
    '& $textWrapper': {
      padding: '0 16px 0 12px',
      marginBottom: '0',
    },
  },
})

type PropsType = {
  /** Card layout - one of 'vertical' | 'horizontal' */
  layout?: CardProps['layout']
  /** Auto layout width - number */
  autoLayoutWidth?: CardProps['autoLayoutWidth']
  staff?: boolean
  following?: boolean
  /** Auto layout width - number */
  avatar?: CardProps['avatar']
  title: string | ReactElement
  titleURL?: string
  subtitle?: string | ReactElement
  btnLabel: string
  onButtonClick?: () => void
  titleTargetUrl?: string
  isLoading?: boolean
}

const CardFollow = ({
  layout,
  autoLayoutWidth,
  staff,
  following,
  avatar,
  title,
  titleURL,
  subtitle,
  btnLabel,
  onButtonClick,
  titleTargetUrl,
  isLoading = false,
}: PropsType): ReactElement => {
  const { ref, width } = useResizeObserver<HTMLDivElement>()
  const classes = useStyles()
  const loaderClasses = useLoaderStyles()

  const switchToHorizontal = width && width > (autoLayoutWidth || 400)

  let isAutoLayoutHorizontal = false
  if (switchToHorizontal) isAutoLayoutHorizontal = true
  if (layout === 'horizontal') isAutoLayoutHorizontal = true
  if (layout === 'vertical') isAutoLayoutHorizontal = false

  return (
    <Wrap
      ref={ref}
      className={clsx(classes.wrap, {
        [loaderClasses.wrapper]: true,
        [classes.isFollowing]: following,
        [classes.isNoAvatar]: !avatar,
      })}
      tabIndex={0}
      role="button"
    >
      <div>
        {isLoading && (
          <div className={loaderClasses.loader}>
            <Loader />
          </div>
        )}
      </div>
      <Grid
        container
        className={clsx(classes.header, {
          [classes.isLayoutHorizontal]: isAutoLayoutHorizontal,
        })}
      >
        {avatar && (
          <Grid item className={classes.avatarWrapper}>
            <div>
              <Avatar
                size={avatar.size}
                imgSrc={avatar.src}
                imgAlt={avatar.alt}
                bgColor={avatar.bgColor}
              >
                {avatar.textAs && avatar.textAs}
              </Avatar>
            </div>
          </Grid>
        )}

        {isAutoLayoutHorizontal && (
          <Grid item xs className={classes.textWrapper}>
            {/* Title */}
            <Typography className={classes.title}>
              <Title
                title={title}
                titleURL={titleURL}
                target={titleTargetUrl}
              />
            </Typography>

            {/* SubTitle */}
            {subtitle && (
              <Typography className={classes.subtitle}>{subtitle}</Typography>
            )}
          </Grid>
        )}

        {staff && (
          <Grid item className={classes.staffWrapper}>
            <svg className={classes.atdBadge} viewBox="0 0 238.65 154.12">
              <defs>
                <linearGradient
                  id="a"
                  x1="-277.78"
                  x2="-276.91"
                  y1="475.2"
                  y2="475.41"
                  gradientTransform="matrix(238.65 0 0 -154.12 66305.31 73347.39)"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0" stopColor="#ffb800" />
                  <stop offset="0.17" stopColor="#ff9000" />
                  <stop offset="0.39" stopColor="#ff6000" />
                  <stop offset="0.5" stopColor="#ff4e00" />
                  <stop offset="0.6" stopColor="#f84502" />
                  <stop offset="0.77" stopColor="#e52d09" />
                  <stop offset="0.97" stopColor="#c60513" />
                  <stop offset="1" stopColor="#c20014" />
                </linearGradient>
              </defs>
              <path
                fill="url(#a)"
                d="M211.61 7v42.14c-40.17 9.21-78.36 19.12-78.36 19.12l-.07-43.66s-13.78 2.71-28.14 7v43.74l-25.25 6.23c-5.38-17.2-19.36-28.36-36.45-28.36a93.08 93.08 0 00-36.65 7.43s1.07 3.85 2.76 8.16 3.43 7.86 3.43 7.86c6.29-2.86 15.8-6 25.25-6 9.25 0 17.32 7 17.32 16.84C22.12 95 1 105.65 1 123.36 1 143.58 15.38 154 43.52 154c16.76 0 30-2.89 38.09-7.21V98.16l23.44-8v35.32c0 14.95 8.84 28.12 29.24 28.12a35.12 35.12 0 0013.91-2.71s.32-3.48.32-8.74-.32-8.56-.32-8.56a17.54 17.54 0 01-6 1.09c-6.34 0-9-4-9-11.37V80.63s19.86-7 39.07-13.31C159 74.66 154.61 95.07 154.61 107c0 32.47 18.37 47.16 47.79 47.16a92.6 92.6 0 0037.21-7.21V0a283.47 283.47 0 00-28 7zM55.31 136.61a24.24 24.24 0 01-10.32 2c-11.14 0-17.62-3.74-17.62-13 0-7.12 8.09-12.7 27.94-18.43zm156.3-1.8a23.58 23.58 0 01-8.64 1.42c-14.24 0-20.54-12.09-20.54-33.17 0-16.46 12-29.61 29.18-34.44z"
                transform="translate(-.96)"
              />
            </svg>
          </Grid>
        )}

        {!isAutoLayoutHorizontal && (
          <Grid item xs={12} className={classes.textWrapper}>
            {/* Title */}
            <Typography className={classes.title}>
              <Title
                variant={'secondary'}
                title={title}
                titleURL={titleURL}
                target={titleTargetUrl}
              />
            </Typography>

            {/* SubTitle */}
            {subtitle && (
              <Typography className={classes.subtitle}>{subtitle}</Typography>
            )}
          </Grid>
        )}
      </Grid>

      <Button
        variant={following ? 'secondary' : 'primary'}
        size="small"
        onClick={onButtonClick}
        className={classes.button}
      >
        <>
          {following ? (
            <Icon glyph="subtract" size={16} />
          ) : (
            <Icon glyph="add" size={16} />
          )}
          <Spacer size={4} />
          {btnLabel}
        </>
      </Button>
    </Wrap>
  )
}

export default CardFollow
